import { useState } from "react";
import "./ProgrammeDocuments.css";

const Icon = ({ path, size = 16, strokeWidth = 2, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {path}
  </svg>
);

const CalendarIcon = (props) => (
  <Icon {...props} path={<><rect x="3.5" y="4.5" width="17" height="16" rx="2" /><path d="M3.5 9.5h17M8 3v3M16 3v3" /></>} />
);
const BookIcon = (props) => (
  <Icon {...props} path={<><path d="M4 4.5C4 4.5 6.5 3.5 9 3.5s5 1 5 1V19c0-.5-2.5-1-5-1s-5 1-5 1V4.5Z" /><path d="M20 4.5C20 4.5 17.5 3.5 15 3.5" /></>} />
);
const Plus = (props) => <Icon {...props} path={<><path d="M12 5v14M5 12h14" /></>} />;
const Trash = (props) => (
  <Icon {...props} path={<><path d="M4 7h16" /><path d="M9 7V4.5h6V7" /><path d="M6 7l1 13h10l1-13" /></>} />
);
const Check = (props) => <Icon {...props} path={<path d="M20 6L9 17l-5-5" />} />;
const AlertCircle = (props) => (
  <Icon {...props} path={<><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></>} />
);
const ArrowLeft = (props) => <Icon {...props} path={<path d="M19 12H5M11 6l-6 6 6 6" />} />;

/* ---------------- Blank row factories ---------------- */

let idCounter = 0;
const nextId = () => `row-${++idCounter}`;

const blankScheduleRow = () => ({
  id: nextId(),
  week: "",
  startDate: "",
  endDate: "",
  title: "",
  format: "Online",
  location: "",
});

const blankHandbookRow = () => ({
  id: nextId(),
  title: "",
  page: "",
  summary: "",
});

/* ---------------- Main component ---------------- */

export default function ProgrammeDocumentsForm() {
  const [stage, setStage] = useState("form"); // form | submitted
  const [schedule, setSchedule] = useState([blankScheduleRow()]);
  const [handbook, setHandbook] = useState([blankHandbookRow()]);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(null);

  /* ---- schedule row helpers ---- */

  const updateSchedule = (id, field, value) => {
    setSchedule((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const addScheduleRow = () => setSchedule((rows) => [...rows, blankScheduleRow()]);

  const removeScheduleRow = (id) =>
    setSchedule((rows) => (rows.length > 1 ? rows.filter((r) => r.id !== id) : rows));

  /* ---- handbook row helpers ---- */

  const updateHandbook = (id, field, value) => {
    setHandbook((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const addHandbookRow = () => setHandbook((rows) => [...rows, blankHandbookRow()]);

  const removeHandbookRow = (id) =>
    setHandbook((rows) => (rows.length > 1 ? rows.filter((r) => r.id !== id) : rows));

  /* ---- validation + submit ---- */

  const validate = () => {
    const nextErrors = {};
    schedule.forEach((r) => {
      if (!r.title.trim()) nextErrors[`sched-title-${r.id}`] = true;
      if (!r.startDate) nextErrors[`sched-start-${r.id}`] = true;
      if (!r.endDate) nextErrors[`sched-end-${r.id}`] = true;
    });
    handbook.forEach((r) => {
      if (!r.title.trim()) nextErrors[`hb-title-${r.id}`] = true;
      if (!r.page.trim()) nextErrors[`hb-page-${r.id}`] = true;
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitted({ schedule, handbook });
    setStage("submitted");
  };

  const editAgain = () => setStage("form");

  /* ---------------- Submitted screen ---------------- */

  if (stage === "submitted" && submitted) {
    return (
      <div className="pf">
        <div className="pf-page">
          <div className="pf-done">
            <div className="pf-done-icon">
              <Check size={26} />
            </div>
            <h1 className="pf-done-title">Programme documents submitted</h1>
            <p className="pf-done-subtitle">
              {submitted.schedule.length} schedule session{submitted.schedule.length === 1 ? "" : "s"} and{" "}
              {submitted.handbook.length} handbook chapter{submitted.handbook.length === 1 ? "" : "s"} were saved.
            </p>

            <div className="pf-done-section">
              <div className="pf-done-section-title">
                <CalendarIcon size={15} /> Schedule
              </div>
              {submitted.schedule.map((r, i) => (
                <div key={r.id} className="pf-done-row">
                  <span className="pf-done-row-index">{i + 1}</span>
                  <div>
                    <div className="pf-done-row-title">{r.title || "Untitled session"}</div>
                    <div className="pf-done-row-meta">
                      {r.week ? `${r.week} · ` : ""}
                      {r.startDate} – {r.endDate} · {r.format}
                      {r.location ? ` · ${r.location}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="pf-done-section">
              <div className="pf-done-section-title">
                <BookIcon size={15} /> Handbook
              </div>
              {submitted.handbook.map((r, i) => (
                <div key={r.id} className="pf-done-row">
                  <span className="pf-done-row-index">{i + 1}</span>
                  <div>
                    <div className="pf-done-row-title">{r.title || "Untitled chapter"}</div>
                    <div className="pf-done-row-meta">
                      Page {r.page}
                      {r.summary ? ` · ${r.summary}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button className="pf-btn pf-btn-ghost pf-done-edit" onClick={editAgain}>
              <ArrowLeft size={15} />
              Edit again
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------- Form screen ---------------- */

  return (
    <div className="pf">
      <div className="pf-page">
        <div className="pf-header">
          <div className="pf-eyebrow">Programme Documents</div>
          <h1 className="pf-title">Add schedule &amp; handbook</h1>
          <p className="pf-subtitle">
            Enter the programme's session schedule and handbook chapters below, then submit to publish them.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Schedule section */}
          <section className="pf-section">
            <div className="pf-section-header">
              <div className="pf-section-heading">
                <CalendarIcon size={17} />
                <h2>Schedule</h2>
              </div>
              <span className="pf-section-count">{schedule.length} session{schedule.length === 1 ? "" : "s"}</span>
            </div>

            {schedule.map((row, i) => (
              <div key={row.id} className="pf-card">
                <div className="pf-card-top">
                  <span className="pf-card-index">Session {i + 1}</span>
                  <button
                    type="button"
                    className="pf-icon-btn"
                    onClick={() => removeScheduleRow(row.id)}
                    disabled={schedule.length === 1}
                    aria-label="Remove session"
                  >
                    <Trash size={15} />
                  </button>
                </div>

                <div className="pf-grid pf-grid-3">
                  <label className="pf-field">
                    <span className="pf-label">Week label</span>
                    <input
                      className="pf-input"
                      placeholder="Week 1"
                      value={row.week}
                      onChange={(e) => updateSchedule(row.id, "week", e.target.value)}
                    />
                  </label>
                  <label className="pf-field">
                    <span className="pf-label">Start date</span>
                    <input
                      type="date"
                      className={`pf-input${errors[`sched-start-${row.id}`] ? " pf-input--error" : ""}`}
                      value={row.startDate}
                      onChange={(e) => updateSchedule(row.id, "startDate", e.target.value)}
                    />
                  </label>
                  <label className="pf-field">
                    <span className="pf-label">End date</span>
                    <input
                      type="date"
                      className={`pf-input${errors[`sched-end-${row.id}`] ? " pf-input--error" : ""}`}
                      value={row.endDate}
                      onChange={(e) => updateSchedule(row.id, "endDate", e.target.value)}
                    />
                  </label>
                </div>

                <label className="pf-field">
                  <span className="pf-label">Session title</span>
                  <input
                    className={`pf-input${errors[`sched-title-${row.id}`] ? " pf-input--error" : ""}`}
                    placeholder="Core concepts workshop"
                    value={row.title}
                    onChange={(e) => updateSchedule(row.id, "title", e.target.value)}
                  />
                </label>

                <div className="pf-grid pf-grid-2">
                  <label className="pf-field">
                    <span className="pf-label">Format</span>
                    <select
                      className="pf-input"
                      value={row.format}
                      onChange={(e) => updateSchedule(row.id, "format", e.target.value)}
                    >
                      <option>Online</option>
                      <option>In person</option>
                      <option>Self-paced</option>
                    </select>
                  </label>
                  <label className="pf-field">
                    <span className="pf-label">Location / details</span>
                    <input
                      className="pf-input"
                      placeholder="Room 204, Main Campus"
                      value={row.location}
                      onChange={(e) => updateSchedule(row.id, "location", e.target.value)}
                    />
                  </label>
                </div>
              </div>
            ))}

            <button type="button" className="pf-btn pf-btn-add" onClick={addScheduleRow}>
              <Plus size={15} />
              Add session
            </button>
          </section>

          {/* Handbook section */}
          <section className="pf-section">
            <div className="pf-section-header">
              <div className="pf-section-heading">
                <BookIcon size={17} />
                <h2>Handbook</h2>
              </div>
              <span className="pf-section-count">{handbook.length} chapter{handbook.length === 1 ? "" : "s"}</span>
            </div>

            {handbook.map((row, i) => (
              <div key={row.id} className="pf-card">
                <div className="pf-card-top">
                  <span className="pf-card-index">Chapter {i + 1}</span>
                  <button
                    type="button"
                    className="pf-icon-btn"
                    onClick={() => removeHandbookRow(row.id)}
                    disabled={handbook.length === 1}
                    aria-label="Remove chapter"
                  >
                    <Trash size={15} />
                  </button>
                </div>

                <div className="pf-grid pf-grid-title-page">
                  <label className="pf-field">
                    <span className="pf-label">Chapter title</span>
                    <input
                      className={`pf-input${errors[`hb-title-${row.id}`] ? " pf-input--error" : ""}`}
                      placeholder="Attendance & participation"
                      value={row.title}
                      onChange={(e) => updateHandbook(row.id, "title", e.target.value)}
                    />
                  </label>
                  <label className="pf-field">
                    <span className="pf-label">Page</span>
                    <input
                      className={`pf-input${errors[`hb-page-${row.id}`] ? " pf-input--error" : ""}`}
                      placeholder="4"
                      value={row.page}
                      onChange={(e) => updateHandbook(row.id, "page", e.target.value)}
                    />
                  </label>
                </div>

                <label className="pf-field">
                  <span className="pf-label">Summary</span>
                  <textarea
                    className="pf-input pf-textarea"
                    rows={2}
                    placeholder="What this chapter covers…"
                    value={row.summary}
                    onChange={(e) => updateHandbook(row.id, "summary", e.target.value)}
                  />
                </label>
              </div>
            ))}

            <button type="button" className="pf-btn pf-btn-add" onClick={addHandbookRow}>
              <Plus size={15} />
              Add chapter
            </button>
          </section>

          {Object.keys(errors).length > 0 && (
            <div className="pf-error-banner">
              <AlertCircle size={16} />
              Please fill in the highlighted required fields before submitting.
            </div>
          )}

          <div className="pf-submit-row">
            <button type="submit" className="pf-btn pf-btn-primary pf-submit-btn">
              Submit programme documents
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
